<template>
    <div>
        <div class="custom-venues-search-wrapper col-12 text-right">
            <base-input id="custom_venues_search_input" v-model="searchQuery" prepend-icon="fas fa-search"
                placeholder="Search..." />
        </div>

        <el-table :data="tableData" row-key="id" id="custom_venues_data_table" v-loading="loading"
            header-row-class-name="thead-light" @sort-change="sortChangeCustomVenues">
            <el-table-column label="Name" min-width="220px" prop="name" sortable="custom">
                <template v-slot="{ row }">
                    <div class="media align-items-center">
                        <div class="media-body">
                            <span class="font-weight-600 name mb-0 text-sm">{{ row.name }}</span>
                        </div>
                    </div>
                </template>
            </el-table-column>

            <el-table-column label="Address" prop="address" min-width="250px">
                <template v-slot="{ row }">
                    {{ formatAddress(row.address) }}
                </template>
            </el-table-column>

            <el-table-column label="City" prop="city" min-width="160px" sortable="custom">
                <template v-slot="{ row }">
                    {{ row.city }}
                </template>
            </el-table-column>

            <el-table-column label="Parent City" prop="parent_city" min-width="160px" sortable="custom">
                <template v-slot="{ row }">
                    {{ row.parent_city }}
                </template>
            </el-table-column>

            <el-table-column label="Was Added By Lead" min-width="150px">
                <template v-slot="{ row }">
                    {{ row.is_added_by_lead ? 'Yes' : 'No' }}
                </template>
            </el-table-column>

            <el-table-column label="Actions" min-width="160px" align="left">
                <template v-slot="{ row }">
                    <el-button size="mini" type="primary" plain @click="openEditDrawer(row)">Edit</el-button>
                    <!-- <el-button size="mini" type="danger" plain @click="confirmDelete(row)">Delete</el-button> -->
                </template>
            </el-table-column>
        </el-table>

        <div class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap mt-3">
            <div>
                <p class="card-category">
                    Showing {{ from + 1 }} to {{ to }} of {{ total }} entries
                </p>
            </div>
            <base-pagination class="pagination-no-border" v-model="pagination.currentPage"
                :per-page="pagination.perPage" :total="total">
            </base-pagination>
        </div>

        <CreateCustomVenueDrawer :visible.sync="editDrawerVisible" :venue="editingVenue"
            @venue-updated="onVenueUpdated" />
    </div>
</template>

<script>
import { mapState } from "vuex";
import { Table, TableColumn, Button, MessageBox } from "element-ui";
import { BasePagination } from "@/components";
import { CustomVenueService } from "@/services";
import CreateCustomVenueDrawer from "@/views/Widgets/Venues/CreateCustomVenueDrawer";
import tableMixin from "@/helpers/tableMixin";

export default {
    name: "custom-venues-tab",
    mixins: [tableMixin],
    components: {
        BasePagination,
        CreateCustomVenueDrawer,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Button.name]: Button,
    },
    data() {
        return {
            propsToSearch: "name",
            loading: true,
            customVenues: [],
            editDrawerVisible: false,
            editingVenue: null,
            sortOptions: {
                prop: 'name',
                order: 'ascending',
            },
        };
    },
    async created() {
        await this.fetchCustomVenues();
    },
    computed: {
        queriedData() {
            let data = [...this.customVenues];
            if (this.searchQuery) {
                const propsToSearch = this.propsToSearch;
                const searchQuery = this.searchQuery;
                data = data.filter(function (item) {
                    return item[propsToSearch]?.toLowerCase().indexOf(searchQuery.toLowerCase()) !== -1;
                });
            }

            return data;
        },
        tableData() {
            let data = [...this.queriedData];
            data.sort((a, b) => {
                if (this.sortOptions.prop) {
                    const prop = this.sortOptions.prop;
                    const order = this.sortOptions.order === "ascending" ? 1 : -1;
                    return a[prop] > b[prop] ? order : -order;
                }
                return 0;
            });
            data = data.slice(this.from, this.from + this.pagination.perPage);
            return data;
        },
        ...mapState("country/", {
            country: (state) => state.country || 'AU',
        }),
    },
    methods: {
        async fetchCustomVenues() {
            this.loading = true;
            try {
                const response = await CustomVenueService.getAllCustomVenues(this.country);
                console.log(response);
                this.customVenues = response?.venues || [];
            } catch (error) {
                this.$store.dispatch("alert/error", error, { root: true });
            } finally {
                this.loading = false;
            }
        },
        addCustomVenue(venue) {
            if (!venue) {
                return;
            }
            this.customVenues.unshift(venue);
        },
        openEditDrawer(venue) {
            this.editingVenue = venue;
            this.editDrawerVisible = true;
        },
        onVenueUpdated(venue) {
            this.editDrawerVisible = false;
            if (!venue) {
                return;
            }
            const index = this.customVenues.findIndex((item) => item.id === venue.id);
            if (index !== -1) {
                this.customVenues.splice(index, 1, venue);
            }
        },
        confirmDelete(venue) {
            MessageBox.confirm(`Delete "${venue.name}"? This cannot be undone.`, 'Delete Custom Venue', {
                distinguishCancelAndClose: true,
                confirmButtonText: 'Delete',
                cancelButtonText: 'Cancel',
                type: 'warning',
            }).then(async () => {
                this.loading = true;
                try {
                    await CustomVenueService.deleteCustomVenue(venue.id);
                    this.customVenues = this.customVenues.filter((item) => item.id !== venue.id);
                    this.$toast.success("Custom venue deleted successfully.");
                } catch (error) {
                    this.$store.dispatch("alert/error", error, { root: true });
                } finally {
                    this.loading = false;
                }
            }).catch(() => {});
        },
        // Address may arrive as an object ({ address }) or a plain string.
        formatAddress(address) {
            if (!address) {
                return "";
            }
            return typeof address === "object" ? (address.address || "") : address;
        },
        sortChangeCustomVenues({ prop, order }) {
            this.sortOptions.prop = prop;
            this.sortOptions.order = order;
            this.pagination.currentPage = 1;
        },
    },
    watch: {
        searchQuery() {
            this.pagination.currentPage = 1;
        },
    },
};
</script>

<style scoped>
.el-table .cell {
    word-break: normal;
}

.custom-venues-search-wrapper {
    margin-top: 10px;
}
</style>
